/**
 * Вход по телефону и коду.
 *
 * Два шага в одном экране: телефон → код. Возврат к шагу «телефон» доступен
 * всегда — скаут ошибается номером чаще, чем кодом.
 */
import { useState, type FormEvent } from 'react'
import { ApiError, NetworkError } from '@/lib/http'
import { useAuth } from '@/lib/auth'

type Step = 'phone' | 'code'

function messageFor(e: unknown): string {
  if (e instanceof NetworkError) return 'Нет связи. Проверьте интернет и попробуйте снова'
  if (e instanceof ApiError) {
    if (e.reason === 'OTP_INVALID') return 'Неверный код'
    if (e.reason === 'OTP_EXPIRED') return 'Код истёк, запросите новый'
    if (e.reason === 'USER_BANNED') return 'Доступ закрыт. Обратитесь к диспетчеру'
    return e.message
  }
  return 'Не удалось войти'
}

export default function LoginPage() {
  const { requestCode, verifyCode } = useAuth()
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function onSubmitPhone(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await requestCode(phone.trim())
      setStep('code')
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await verifyCode(phone.trim(), code.trim())
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center gap-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Aida Скаут</h1>
        <p className="mt-1 text-sm opacity-70">
          {step === 'phone' ? 'Введите номер телефона' : `Код отправлен на ${phone}`}
        </p>
      </header>

      {step === 'phone' ? (
        <form onSubmit={onSubmitPhone} className="flex flex-col gap-3">
          <input
            className="rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-lg"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 700 000 00 00"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value)
            }}
            required
          />
          <button
            className="rounded-lg bg-emerald-500 px-4 py-3 font-medium text-black disabled:opacity-50"
            disabled={busy || phone.trim().length < 10}
          >
            {busy ? 'Отправляем…' : 'Получить код'}
          </button>
        </form>
      ) : (
        <form onSubmit={onSubmitCode} className="flex flex-col gap-3">
          <input
            className="rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-center text-2xl tracking-[0.4em]"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={code}
            onChange={(e) => {
              setCode(e.target.value)
            }}
            required
          />
          <button
            className="rounded-lg bg-emerald-500 px-4 py-3 font-medium text-black disabled:opacity-50"
            disabled={busy || code.trim().length < 4}
          >
            {busy ? 'Проверяем…' : 'Войти'}
          </button>
          <button
            type="button"
            className="py-2 text-sm opacity-70 underline"
            onClick={() => {
              setStep('phone')
              setCode('')
              setError('')
            }}
          >
            Изменить номер
          </button>
        </form>
      )}

      {error ? (
        <p role="alert" className="rounded-lg bg-red-500/15 px-4 py-3 text-sm text-red-300">
          {error}
        </p>
      ) : null}
    </main>
  )
}
